import { PollingIntervals, TabTypes } from "app/utils/const";
import { useCallback, useEffect, useRef, useState } from "react";

type TabType = "LIVE" | "POPULAR" | "ALL";

type UseCompetitionsPollingProps = {
  selectedCompetitionType: TabType;
  time: string;
  singleCompetitionOnly: boolean;
  isIdle: boolean;
  setCompetitions: (options?: { fullList?: boolean }) => void;
};

export const useCompetitionsPolling = ({
  selectedCompetitionType,
  time,
  singleCompetitionOnly,
  isIdle,
  setCompetitions,
}: UseCompetitionsPollingProps) => {
  const [pollIntervalId, setPollIntervalId] = useState<number>(0);
  const intervalRef = useRef<number>(0);
  const prevSelectedCompetitionTypeRef = useRef(selectedCompetitionType);

  const startPolling = useCallback(() => {
    let pollingInterval: number;
    switch (selectedCompetitionType) {
      case TabTypes.LIVE:
        pollingInterval = PollingIntervals.LIVE;
        break;
      case TabTypes.ALL:
        pollingInterval = PollingIntervals.ALL;
        break;
      default:
        return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const intervalId = window.setInterval(() => {
      if (isIdle) return;
      setCompetitions({ fullList: true });
    }, pollingInterval);

    intervalRef.current = intervalId;
    setPollIntervalId(intervalId);
  }, [selectedCompetitionType, isIdle, setCompetitions]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = 0;
      setPollIntervalId(0);
    }
  }, []);

  // handle polling lifecycle
  useEffect(() => {
    const shouldPoll =
      !singleCompetitionOnly &&
      !isIdle &&
      (selectedCompetitionType === TabTypes.LIVE ||
        selectedCompetitionType === TabTypes.ALL);

    const prevType = prevSelectedCompetitionTypeRef.current;
    const tabChanged = selectedCompetitionType !== prevType;
    prevSelectedCompetitionTypeRef.current = selectedCompetitionType;

    if (shouldPoll) {
      if (tabChanged || !intervalRef.current) {
        startPolling();
      }
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [
    selectedCompetitionType,
    singleCompetitionOnly,
    isIdle,
    startPolling,
    stopPolling,
  ]);

  return { pollIntervalId };
};
